import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Select,
  HStack,
  VStack,
  Avatar,
  Flex,
  Spacer,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
} from '@chakra-ui/react';
import { SearchIcon, ViewIcon } from '@chakra-ui/icons';
import axios from 'axios';

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, filterType]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) throw new Error('Unauthorized');
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_BASE_URL ||
        window.location.origin ||
        'http://localhost:3030'
      )
        .trim()
        .replace(/\/$/, '');
      const typeParam =
        filterType && filterType !== 'all' ? `&type=${filterType}` : '';
      const response = await axios.get(
        `${apiBaseUrl}/api/admin/transactions?page=${currentPage}&limit=20${typeParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        setTransactions(response.data.data);
        setTotalPages(response.data.totalPages);
      } else {
        setError('Failed to fetch transactions');
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (tx) => {
    setSelectedTransaction(tx);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  const filteredTransactions = transactions.filter(
    (transaction) =>
      transaction.storyTitle
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.chapterTitle
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.userId?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  const formatCurrency = (amount) => {
    return `${amount} 💎`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="400px"
      >
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Error!</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box p={6}>
      <Heading mb={6} size="lg">
        Transaction History
      </Heading>

      {/* Summary Cards */}
      <Flex gap={4} mb={6} wrap="wrap">
        <Card
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          shadow="md"
          flex={1}
          minW="200px"
        >
          <CardBody>
            <Text fontSize="sm" color="gray.600">
              Total Transactions
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.500">
              {transactions.length}
            </Text>
          </CardBody>
        </Card>
        <Card
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          shadow="md"
          flex={1}
          minW="200px"
        >
          <CardBody>
            <Text fontSize="sm" color="gray.600">
              Total Revenue
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.500">
              {formatCurrency(
                transactions.reduce((sum, t) => sum + t.totalAmount, 0),
              )}
            </Text>
          </CardBody>
        </Card>
        <Card
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          shadow="md"
          flex={1}
          minW="200px"
        >
          <CardBody>
            <Text fontSize="sm" color="gray.600">
              Admin Share
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="purple.500">
              {formatCurrency(
                transactions.reduce((sum, t) => sum + t.adminShare, 0),
              )}
            </Text>
          </CardBody>
        </Card>
      </Flex>

      {/* Filters */}
      <Card
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        shadow="md"
        mb={6}
      >
        <CardBody>
          <HStack spacing={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              maxW="200px"
            >
              <option value="all">All Transactions</option>
              <option value="chapter_audio_purchase">Audio Purchases</option>
            </Select>
          </HStack>
        </CardBody>
      </Card>

      {/* Transactions Table */}
      <Card bg={bgColor} border="1px" borderColor={borderColor} shadow="md">
        <CardHeader>
          <Heading size="md">Recent Transactions</Heading>
        </CardHeader>
        <CardBody>
          {filteredTransactions.length === 0 ? (
            <Text textAlign="center" py={8} color="gray.500">
              No transactions found.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>User</Th>
                    <Th>Story & Chapter</Th>
                    <Th>Amount</Th>
                    <Th>Shares</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredTransactions.map((transaction) => (
                    <Tr key={transaction._id}>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {formatDate(transaction.createdAt)}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <HStack>
                          <Avatar
                            size="sm"
                            name={transaction.userId?.name}
                            src={transaction.userId?.profileImage}
                          />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {transaction.userId?.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {transaction.userId?.email}
                            </Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {transaction.storyTitle || 'Unknown Story'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {transaction.chapterTitle || 'Unknown Chapter'}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Badge colorScheme="blue" fontSize="sm">
                          {formatCurrency(transaction.totalAmount)}
                        </Badge>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="xs" color="green.500">
                            Creator: {formatCurrency(transaction.userShare)}
                          </Text>
                          <Text fontSize="xs" color="blue.500">
                            Admin: {formatCurrency(transaction.adminShare)}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Badge colorScheme="green" fontSize="xs">
                          Completed
                        </Badge>
                      </Td>
                      <Td>
                        <Tooltip label="View Details">
                          <IconButton
                            size="sm"
                            icon={<ViewIcon />}
                            onClick={() => openModal(transaction)}
                            variant="ghost"
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Flex justify="center" mt={6} gap={2}>
              <Button
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>
              <Text alignSelf="center" mx={4}>
                Page {currentPage} of {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Flex>
          )}
        </CardBody>
      </Card>

      {/* Transaction Detail Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Transaction Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedTransaction ? (
              <>
                <VStack align="start" spacing={2}>
                  <Text>
                    <strong>Date:</strong> {formatDate(selectedTransaction.createdAt)}
                  </Text>
                  <Text>
                    <strong>User:</strong> {selectedTransaction.userId?.name} (
                    {selectedTransaction.userId?.email})
                  </Text>
                  <Text>
                    <strong>Story:</strong> {selectedTransaction.storyTitle}
                  </Text>
                  <Text>
                    <strong>Chapter:</strong> {selectedTransaction.chapterTitle}
                  </Text>
                  <Divider />
                  <Text>
                    <strong>Total Amount:</strong>{' '}
                    {formatCurrency(selectedTransaction.totalAmount)}
                  </Text>
                  <Text>
                    <strong>Creator Share:</strong>{' '}
                    {formatCurrency(selectedTransaction.userShare)}
                  </Text>
                  <Text>
                    <strong>Admin Share:</strong>{' '}
                    {formatCurrency(selectedTransaction.adminShare)}
                  </Text>
                  <Text>
                    <strong>Status:</strong> Completed
                  </Text>
                </VStack>
              </>
            ) : (
              <Text>Loading...</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeModal}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TransactionHistory;
